/**
 * apps/customer/src/lib/turnstile.ts
 *
 * SIL-1050: real Cloudflare Turnstile widget for the public /signup page —
 * server-side verification already exists (apps/api/src/routes/pub/signup.ts
 * -> services/referral/turnstile.ts). Mirrors apps/fmv/src/client.ts's
 * mountTurnstile (same script-load + render pattern) but adds a `reset`
 * helper: signup's own resend flow needs a SECOND, independent widget
 * instance whose token is captured separately from the initial-submit
 * widget (Turnstile tokens are single-use — design spec's "Anti-abuse"), and
 * that widget's container is NOT torn down/rebuilt between resends the way
 * fmv's vanilla-DOM render() replaces its container's innerHTML on every
 * call — React's signup.tsx keeps the verify-step form mounted across
 * resends, so getting a fresh token means resetting the existing widget
 * instance, not re-rendering a new one.
 *
 * FAILS VISIBLE, not silent: unlike the FMV widget (which falls back to
 * Cloudflare's always-passing test site key whenever VITE_FMV_TURNSTILE_
 * SITE_KEY is unset — fine for a marketing-embeddable, low-stakes tool),
 * signup is an account-creation flow. An unset site key in a real deploy
 * must disable the form and show a clear message, never silently send a
 * placeholder token that the API's fail-closed verifySignupTurnstileToken
 * would reject anyway (503/treated-as-unverified, with no explanation to
 * the visitor). The dev-mode fallback below is scoped to
 * `import.meta.env.DEV` specifically (a real local `vite dev` server), not
 * "unset for any reason" — a production build that shipped without the site
 * key baked in (scripts/aws/deploy-customer.sh) must never quietly pass as
 * configured.
 *
 * SIL-1344: reads VITE_SIGNUP_TURNSTILE_SITE_KEY, renamed from
 * VITE_CLIENT_TURNSTILE_SITE_KEY (no runtime fallback needed here: Vite
 * bakes exactly one value into the bundle at build time).
 */

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Cloudflare's published always-passes test site key — see
// https://developers.cloudflare.com/turnstile/troubleshooting/testing/.
// Same literal apps/fmv/src/client.ts uses for its own local-dev fallback.
const DEV_TEST_SITE_KEY = '1x00000000000000000000AA';

const configuredSiteKey = (import.meta.env.VITE_SIGNUP_TURNSTILE_SITE_KEY as string | undefined) || '';

export const TURNSTILE_SITE_KEY = configuredSiteKey || (import.meta.env.DEV ? DEV_TEST_SITE_KEY : '');

/** True once there is a usable site key — real, or the DEV-only fallback.
 * False means this build was published without VITE_SIGNUP_TURNSTILE_SITE_KEY
 * baked in; signup.tsx must disable its forms and say so rather than ever
 * calling the API with an empty/placeholder token. */
export const TURNSTILE_CONFIGURED = TURNSTILE_SITE_KEY !== '';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('turnstile script failed to load')));
    document.head.appendChild(script);
  });
  return turnstileScriptPromise;
}

/** Mounts a Turnstile widget into `containerId` (must already be in the DOM)
 * and invokes `onVerify(token)` once solved, and again with '' on expiry/
 * error so callers can re-disable whatever the token was gating. Returns the
 * widget id (pass to `resetTurnstile` for a fresh token) or null if it never
 * mounted — no site key configured, missing container, or script-load
 * failure all fail closed: no widget, no token, caller stays gated. */
export async function mountTurnstile(containerId: string, onVerify: (token: string) => void): Promise<string | null> {
  if (!TURNSTILE_CONFIGURED) return null;
  const container = document.getElementById(containerId);
  if (!container) return null;

  try {
    await loadTurnstileScript();
  } catch {
    return null;
  }

  if (!window.turnstile) return null;
  return window.turnstile.render(container, {
    sitekey: TURNSTILE_SITE_KEY,
    callback: onVerify,
    'expired-callback': () => onVerify(''),
    'error-callback': () => onVerify(''),
  });
}

/** Resets a previously-mounted widget so it mints a fresh, single-use token
 * — call after consuming a widget's token (e.g. a resend), since that
 * widget's container stays mounted across React re-renders. No-op if
 * turnstile.js never loaded or the widget id is null. */
export function resetTurnstile(widgetId: string | null): void {
  if (!widgetId) return;
  window.turnstile?.reset(widgetId);
}
